import React, { ReactNode } from 'react';

import { Box, Button, Flex, Icon, Separator, Text } from '@chakra-ui/react';
import { MetropolisLogoWordmark, Menu } from '@m/icons';

export interface PageHeaderProps {
  /** Center content. Can be logo, title, or custom content */
  centerContent?: ReactNode;
  /** Optional cobrand. Will appear next to the center content  */
  cobrand?: ReactNode;
  /** Optional left icon. Defaults to hamburger menu */
  leftIcon?: ReactNode;
  /** Handler for left icon click */
  onLeftIconClick?: () => void;
  /** Handler for logo / center content click (e.g. navigate home) */
  onLogoClick?: () => void;
  /** Optional right button. Provide either onClick for a button, or href (with optional target) for a link. */
  rightAction?: {
    href?: string;
    label: string;
    onClick?: () => void;
    target?: string;
  };
  /** Bottom divider under the header. Defaults to true. */
  showBorder?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  leftIcon,
  centerContent,
  cobrand,
  rightAction,
  onLeftIconClick,
  onLogoClick,
  showBorder = true,
}) => {
  const defaultLeftIcon = <Icon as={Menu} width={8} height={8} color="text.primary" />;

  const defaultCenterContent = (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      <MetropolisLogoWordmark height={32} width={120} />
    </Box>
  );

  return (
    <Box
      bg="fill.background.default"
      borderBottom={showBorder ? '1px solid' : undefined}
      borderColor={showBorder ? 'stroke.divider.default' : undefined}
      px={4}
      py={4.5}
      height={20}
      position="relative"
      width="100%"
    >
      <Flex align="center" justify="space-between" h="full" w="full" minW={0} gap={2}>
        {/* Left Icon */}
        <Box flexShrink={0} width={8} height={8}>
          {(leftIcon || onLeftIconClick) && (
            <Box
              as="button"
              aria-label={leftIcon ? 'Toggle menu' : 'Open menu'}
              onClick={onLeftIconClick}
              w="full"
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              cursor={onLeftIconClick ? 'pointer' : 'default'}
            >
              {leftIcon || defaultLeftIcon}
            </Box>
          )}
        </Box>

        {/* Center Content */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          px={2}
          height="100%"
          flex="1"
          minW={0}
          overflow="hidden"
        >
          {cobrand}
          {cobrand && <Separator height="100%" mx={2} orientation="vertical" size="sm" />}
          {onLogoClick ? (
            <Box
              as="button"
              aria-label="Go to home"
              onClick={onLogoClick}
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              cursor="pointer"
              bg="transparent"
              border="none"
              p={0}
            >
              {centerContent || defaultCenterContent}
            </Box>
          ) : (
            centerContent || defaultCenterContent
          )}
        </Box>

        {/* Right Action */}
        <Box flexShrink={0} minW={12}>
          {rightAction &&
            (rightAction.href ? (
              <Button asChild variant="secondary" size="sm" px={3} py={2} h={9} borderRadius="sm">
                <a
                  href={rightAction.href}
                  target={rightAction.target}
                  rel={rightAction.target === '_blank' ? 'noopener noreferrer' : undefined}
                >
                  <Text variant="SmallBodyMedium">{rightAction.label}</Text>
                </a>
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={rightAction.onClick}
                px={3}
                py={2}
                h={9}
                borderRadius="sm"
              >
                <Text variant="SmallBodyMedium">{rightAction.label}</Text>
              </Button>
            ))}
        </Box>
      </Flex>
    </Box>
  );
};

export default PageHeader;
